// src/pages/DiscussPage.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Nav from '../components/Nav';
import Footer from '../components/Footer';
import { useAuthContext } from '../contexts/AuthContext';
import config from '../config';
import './DiscussPage.css';

export default function DiscussPage() {
    const { user, API_URL } = useAuthContext();
    const [searchParams] = useSearchParams();
    const [channels, setChannels] = useState([]);
    const [selectedChannel, setSelectedChannel] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const messagesEndRef = useRef(null);

    useEffect(() => {
        document.title = `Discuss | ${config.siteTitle}`;
    }, []);

    // Fetch channels
    useEffect(() => {
        const fetchChannels = async () => {
            try {
                const response = await fetch(`${API_URL}/api/channels`, {
                    credentials: 'include'
                });
                if (response.ok) {
                    const data = await response.json();
                    setChannels(data.channels || []);
                }
            } catch (err) {
                console.error('Failed to fetch channels:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchChannels();
    }, [API_URL]);

    // Handle initial channel selection
    useEffect(() => {
        if (channels.length === 0) return;

        const channelParam = searchParams.get('channel');

        // If we have a param, try to sync with it
        if (channelParam) {
            const targetChannel = channels.find(c => c.id === parseInt(channelParam));
            if (targetChannel && targetChannel.id !== selectedChannel?.id) {
                setSelectedChannel(targetChannel);
            }
        }
        // Otherwise if nothing selected, default to first
        else if (!selectedChannel) {
            setSelectedChannel(channels[0]);
        }
    }, [channels, searchParams, selectedChannel]);

    // Fetch messages for selected channel
    const fetchMessages = useCallback(async () => {
        if (!selectedChannel) return;

        try {
            const response = await fetch(
                `${API_URL}/api/posts?channel_id=${selectedChannel.id}`,
                { credentials: 'include' }
            );
            if (response.ok) {
                const data = await response.json();
                // Reverse to show oldest first (chat style)
                setMessages((data.posts || []).reverse());
            }
        } catch (err) {
            console.error('Failed to fetch messages:', err);
        }
    }, [API_URL, selectedChannel]);

    useEffect(() => {
        fetchMessages();
    }, [fetchMessages]);

    // Scroll to bottom when messages change
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!newMessage.trim() || !selectedChannel || sending) return;

        setSending(true);
        try {
            const response = await fetch(`${API_URL}/api/posts`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    content: newMessage.trim(),
                    channel_id: selectedChannel.id
                })
            });

            if (response.ok) {
                setNewMessage('');
                fetchMessages();
            }
        } catch (err) {
            console.error('Failed to send message:', err);
        } finally {
            setSending(false);
        }
    };

    const handleChannelSelect = (channel) => {
        setSelectedChannel(channel);
        setSidebarOpen(false);
    };

    const formatTime = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        if (date.toDateString() === today.toDateString()) return 'Today';
        if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    // Group messages by date
    const groupedMessages = messages.reduce((groups, msg) => {
        const date = formatDate(msg.created_at);
        if (!groups[date]) groups[date] = [];
        groups[date].push(msg);
        return groups;
    }, {});

    return (
        <div className="discuss-page">
            <Nav />

            <div className="discuss-container">
                {/* Mobile channel toggle */}
                <button
                    className="channel-toggle"
                    onClick={() => setSidebarOpen(!sidebarOpen)}
                >
                    <span className="channel-toggle-icon">#</span>
                    <span>{selectedChannel?.name || 'Channels'}</span>
                </button>

                {/* Sidebar */}
                <aside className={`discuss-sidebar ${sidebarOpen ? 'open' : ''}`}>
                    <div className="sidebar-header">
                        <h2>Channels</h2>
                    </div>
                    <nav className="channel-list">
                        {channels.map(channel => (
                            <button
                                key={channel.id}
                                className={`channel-item ${selectedChannel?.id === channel.id ? 'active' : ''}`}
                                onClick={() => handleChannelSelect(channel)}
                            >
                                <span className="channel-hash">#</span>
                                <span className="channel-name">{channel.name}</span>
                            </button>
                        ))}
                    </nav>
                </aside>

                {/* Main chat area */}
                <main className="discuss-main">
                    {loading ? (
                        <div className="loading">Loading...</div>
                    ) : !selectedChannel ? (
                        <div className="empty-state">
                            <p>Select a channel to start chatting</p>
                        </div>
                    ) : (
                        <>
                            {/* Channel header */}
                            <header className="chat-header">
                                <h2 className="chat-channel-name">
                                    <span className="channel-hash">#</span>
                                    {selectedChannel.name}
                                </h2>
                                {selectedChannel.description && (
                                    <p className="chat-channel-desc">{selectedChannel.description}</p>
                                )}
                            </header>

                            {/* Messages */}
                            <div className="messages-container">
                                {Object.entries(groupedMessages).map(([date, msgs]) => (
                                    <div key={date} className="message-group">
                                        <div className="date-divider">
                                            <span>{date}</span>
                                        </div>
                                        {msgs.map(msg => (
                                            <div
                                                key={msg.id}
                                                className={`message ${msg.user_id === user?.id ? 'own' : ''}`}
                                            >
                                                <Link to={`/profile/${msg.user_id}`} className="message-avatar">
                                                    {msg.user?.username?.[0]?.toUpperCase() || '?'}
                                                </Link>
                                                <div className="message-content">
                                                    <div className="message-header">
                                                        <Link to={`/profile/${msg.user_id}`} className="message-author">
                                                            {msg.user?.username || 'Unknown'}
                                                        </Link>
                                                        <span className="message-time">
                                                            {formatTime(msg.created_at)}
                                                        </span>
                                                    </div>
                                                    <p className="message-text">{msg.content}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ))}
                                <div ref={messagesEndRef} />
                            </div>

                            {/* Message input */}
                            <form className="message-form" onSubmit={handleSendMessage}>
                                <input
                                    type="text"
                                    className="message-input"
                                    placeholder={`Message #${selectedChannel.name}`}
                                    value={newMessage}
                                    onChange={(e) => setNewMessage(e.target.value)}
                                    maxLength={280}
                                    disabled={sending}
                                />
                                <button
                                    type="submit"
                                    className="send-button"
                                    disabled={!newMessage.trim() || sending}
                                >
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <line x1="22" y1="2" x2="11" y2="13"></line>
                                        <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                                    </svg>
                                </button>
                            </form>
                        </>
                    )}
                </main>
            </div>

            <Footer />
        </div>
    );
}
